import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const NOTIFICATION_OPTIONS = [
  { key: 'emailProjectUpdates', label: 'Project updates via email' },
  { key: 'emailBidAlerts', label: 'Bid status alerts via email' },
  { key: 'emailTeamInvites', label: 'Team invitation notifications' },
  { key: 'dashboardDigest', label: 'Weekly dashboard digest' },
];

const ProfileSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, updateProfile } = useAuth();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    fullName: userProfile?.full_name || '',
    phone: userProfile?.phone || '',
    title: userProfile?.title || '',
    notifications: {
      emailProjectUpdates: true,
      emailBidAlerts: true,
      emailTeamInvites: true,
      dashboardDigest: false,
      ...(userProfile?.preferences?.notifications || {}),
    },
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(userProfile?.avatar_url || null);
  const [avatarSignedUrl, setAvatarSignedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);

  const isPostAcceptance = location?.state?.fromInvitation || false;

  useEffect(() => {
    if (userProfile?.avatar_url && !userProfile?.avatar_url?.startsWith('http')) {
      // It's a storage path — get signed URL
      supabase?.storage?.from('avatars')?.createSignedUrl(userProfile?.avatar_url, 3600)?.then(({ data }) => {
          if (data?.signedUrl) setAvatarSignedUrl(data?.signedUrl);
        });
    } else if (userProfile?.avatar_url) {
      setAvatarSignedUrl(userProfile?.avatar_url);
    }
  }, [userProfile]);

  const handleAvatarChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (file?.size > 2 * 1024 * 1024) {
      setError('Avatar must be under 2MB.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif']?.includes(file?.type)) {
      setError('Please upload a JPEG, PNG, WebP, or GIF image.');
      return;
    }

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setError('');
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !user?.id) return null;
    setUploadProgress(true);
    try {
      const ext = avatarFile?.name?.split('.')?.pop();
      const filePath = `${user?.id}/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase?.storage
        ?.from('avatars')
        ?.upload(filePath, avatarFile, { upsert: true });
      if (uploadError) throw uploadError;
      return filePath;
    } finally {
      setUploadProgress(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      let avatarPath = userProfile?.avatar_url || null;

      if (avatarFile) {
        avatarPath = await uploadAvatar();
      }

      const updates = {
        full_name: formData?.fullName?.trim(),
        phone: formData?.phone?.trim() || null,
        title: formData?.title?.trim() || null,
        avatar_url: avatarPath,
        preferences: {
          notifications: formData?.notifications,
        },
        profile_completed: true,
      };

      const { error: updateError } = await updateProfile(updates);
      if (updateError) throw new Error(updateError?.message || 'Failed to save profile.');

      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 1800);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  const displayAvatar = avatarPreview || avatarSignedUrl;

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto py-6">
        {/* Header */}
        <div className="mb-8">
          {isPostAcceptance && (
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm font-medium px-3 py-1.5 rounded-full mb-4">
              <Icon name="CheckCircle" size={14} />
              Account created successfully!
            </div>
          )}
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">Complete Your Profile</h1>
          <p className="text-muted-foreground mt-1.5">
            Help your team know who you are by filling in your details.
          </p>
        </div>

        {success ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={32} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Profile saved!</h2>
            <p className="text-gray-600 text-sm">Redirecting you to the dashboard...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Avatar Upload */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Camera" size={18} className="text-muted-foreground" />
                Profile Photo
              </h2>
              <div className="flex items-center gap-5">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-muted border-2 border-border flex items-center justify-center">
                    {displayAvatar ? (
                      <img src={displayAvatar} alt="Avatar preview" className="w-full h-full object-cover" />
                    ) : (
                      <Icon name="User" size={32} className="text-muted-foreground" />
                    )}
                  </div>
                  {uploadProgress && (
                    <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                      <Icon name="Loader2" size={20} className="text-white animate-spin" />
                    </div>
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef?.current?.click()}
                  >
                    <Icon name="Upload" size={14} className="mr-1.5" />
                    {displayAvatar ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1.5">JPEG, PNG, WebP or GIF · Max 2MB</p>
                </div>
              </div>
            </div>

            {/* Personal Info */}
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Icon name="User" size={18} className="text-muted-foreground" />
                Personal Information
              </h2>
              <Input
                label="Full Name"
                type="text"
                placeholder="e.g. John Smith"
                value={formData?.fullName}
                onChange={(e) => setFormData(prev => ({ ...prev, fullName: e?.target?.value }))}
                required
              />
              <Input
                label="Phone Number"
                type="tel"
                placeholder="e.g. +1 (555) 000-0000"
                value={formData?.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e?.target?.value }))}
                description="Used for team communications"
              />
              <Input
                label="Job Title"
                type="text"
                placeholder="e.g. Project Manager, Site Supervisor"
                value={formData?.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
                description="Your role at the company"
              />
            </div>

            {/* Notification Preferences */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h2 className="text-base font-semibold text-foreground mb-1 flex items-center gap-2">
                <Icon name="Bell" size={18} className="text-muted-foreground" />
                Notification Preferences
              </h2>
              <p className="text-sm text-muted-foreground mb-4">Choose what you'd like to be notified about.</p>
              <div className="space-y-3">
                {NOTIFICATION_OPTIONS?.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-3 cursor-pointer group">
                    <div
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        notifications: { ...prev?.notifications, [key]: !prev?.notifications?.[key] }
                      }))}
                      className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 relative cursor-pointer ${
                        formData?.notifications?.[key] ? 'bg-amber-500' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                        formData?.notifications?.[key] ? 'translate-x-5' : 'translate-x-1'
                      }`} />
                    </div>
                    <span className="text-sm text-foreground group-hover:text-foreground/80">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleSkip}
                className="flex-1"
                disabled={loading}
              >
                Skip for now
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Icon name="Save" size={16} />
                    Save Profile
                  </span>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </MainLayout>
  );
};

export default ProfileSetup;
